import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Hidden, Visible } from '@leroy-merlin-br/backyard-react-ui'

import slugify from 'scripts/utils/slugify'
import {
  setProductImpressionItems,
  onProductClick,
  pushDataObject
} from 'scripts/utils/data-layer'
import SalesClick from 'scripts/utils/dataLayer/Sales/SalesClick'

import { ProductThumb } from 'reactGarden/ProductThumb'
import { GenericCarousel } from 'reactGarden/GenericCarousel'
import AssistedSaleProductList from 'reactGarden/AssistedSaleProductList'

import { ContextVisibility } from 'reactShared/components/ContextVisibility'

import WishlistProviderMethods from 'reactSite/Wishlist/provider'

import { useProfile } from 'catalog/hooks/use-profile'

import * as S from './styled'

const ProductsCarousel = ({
  products,
  title,
  canAddProductsToWishlist = true,
  additionalProductContent = null
}) => {
  const { profile } = useProfile()

  const formattedTitle = useMemo(() => slugify(title, '_'), [title])

  const sendSalesClick = (product, type, position, title) => {
    const salesClick = new SalesClick(product, type, position, title)

    salesClick.sendClickToDatalayer()
  }

  const onClickProductThumb = (product, options) => {
    onProductClick(product, options)

    sendSalesClick(product, 'offers', options.position, title)
  }

  const renderCarousel = () => (
    <GenericCarousel slidesPerView="auto" spaceBetween={5}>
      {products.map((product, index) => (
        <S.WrapperItem
          className="wrapper-carousel-item"
          key={`product-thumb-${product.id}`}
        >
          <ProductThumb
            onClick={() =>
              onClickProductThumb(product, {
                list: formattedTitle,
                position: index + 1
              })
            }
            product={product}
            position={index + 1}
            showAddToWishlistIcon={canAddProductsToWishlist}
            additionalContent={additionalProductContent}
          />
        </S.WrapperItem>
      ))}
    </GenericCarousel>
  )

  useEffect(() => {
    if (formattedTitle && profile.region && products) {
      const dataObject = {
        event: 'productImpression',
        dimension14: profile && profile.region,
        ecommerce: {
          currencyCode: 'BRL',
          impressions: [...setProductImpressionItems(products, formattedTitle)]
        }
      }

      pushDataObject(dataObject)
    }
  }, [formattedTitle, profile, products])

  return (
    <WishlistProviderMethods items={products}>
      <div itemScope itemType="http://schema.org/ItemList">
        <ContextVisibility when={['ecommerce', 'app']}>
          {renderCarousel()}
        </ContextVisibility>

        <ContextVisibility when={['assisted-sale']}>
          <Hidden untilKilo kilo>
            {renderCarousel()}
          </Hidden>
        </ContextVisibility>

        <ContextVisibility when={['assisted-sale']}>
          <Visible untilKilo kilo>
            <AssistedSaleProductList products={products} />
          </Visible>
        </ContextVisibility>
      </div>
    </WishlistProviderMethods>
  )
}

ProductsCarousel.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      shortName: PropTypes.string.isRequired,
      unit: PropTypes.string.isRequired,
      inWishList: PropTypes.bool.isRequired,
      price: PropTypes.object.isRequired,
      installments: PropTypes.object,
      tag: PropTypes.object,
      offer: PropTypes.object,
      url: PropTypes.string.isRequired
    })
  ).isRequired,
  title: PropTypes.string.isRequired
}

export default ProductsCarousel
